import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { useSettings } from '../../context/SettingsContext.jsx';
import { useTheme } from '../../context/ThemeContext.jsx';
import { uploadUrl } from '../../api/client.js';
import { MORPH_BTN } from '../../lib/ui.js';

export default function AdminLogin() {
  const { login } = useAuth();
  const { settings } = useSettings();
  const { dark, toggleDark } = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '', captcha: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsCaptcha, setNeedsCaptcha] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  const useLogo = settings.app_logo_mode === 'logo' && !!settings.app_logo;

  const refreshCaptcha = () => setCaptchaKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      console.log('📝 Submitting login form...');
      
      // Panggil login dengan 3 parameter
      const result = await login(
        form.username, 
        form.password, 
        needsCaptcha ? form.captcha : undefined
      );
      
      console.log('📥 Login result:', result);
      
      // Cek hasil login
      if (result && result.success) {
        console.log('✅ Login success, redirecting...');
        sessionStorage.setItem('epw_just_logged_in', '1');
        navigate('/admin/dashboard');
      } else {
        // Jika gagal, cek apakah perlu captcha
        if (result?.needsCaptcha || result?.response?.needsCaptcha) {
          setNeedsCaptcha(true);
          refreshCaptcha();
          setForm((f) => ({ ...f, captcha: '' }));
        }
        setError(result?.error || 'Login gagal. Silakan coba lagi.');
      }
    } catch (err) {
      console.error('❌ Submit error:', err);
      
      const data = err.response?.data;
      setError(data?.message || 'Login gagal. Silakan coba lagi.');
      
      if (data?.needsCaptcha) {
        setNeedsCaptcha(true);
        refreshCaptcha();
        setForm((f) => ({ ...f, captcha: '' }));
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full pl-11 pr-4 py-3 rounded-xl border text-sm outline-none bg-white dark:bg-neutral-800 dark:text-white border-neutral-300 dark:border-neutral-700 focus:outline-none focus:ring-4 focus:ring-forest-500/15 focus:border-forest-500 transition-all';

  return (
    <div className="fixed inset-0 flex items-start md:items-center justify-center px-4 py-6 overflow-y-auto bg-gradient-to-br from-[#f0f7ec] to-[#fff9e6] dark:from-neutral-950 dark:to-neutral-900 transition-colors duration-300">
      <div className="w-full max-w-5xl md:h-[min(90vh,640px)] overflow-hidden rounded-[2rem] shadow-2xl border bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 my-auto">
        <div className="grid md:grid-cols-2 md:h-full md:grid-rows-[minmax(0,1fr)]">

          {/* Sisi Kiri: Branding */}
          <div className="min-h-0 relative flex flex-col justify-center items-center text-center px-6 py-8 md:p-14 text-white overflow-y-auto"
               style={{ background: `linear-gradient(135deg, ${settings.app_color || 'var(--accent)'}, var(--color-forest-600))` }}>
            <button
              onClick={toggleDark}
              type="button"
              className="absolute top-5 right-5 w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ease-in-out bg-white/15 hover:rounded-xl active:rounded-3xl active:scale-[0.98] hover:bg-white/25"
            >
              <span className="material-symbols-outlined text-lg">{dark ? 'light_mode' : 'bedtime'}</span>
            </button>

            {useLogo ? (
              <img
                src={uploadUrl(settings.app_logo)}
                alt={settings.site_name}
                className="mb-6 object-contain"
                style={{ height: '5.5rem', width: 'auto', maxWidth: '80%' }}
              />
            ) : (
              <div className="w-16 h-16 md:w-24 md:h-24 rounded-3xl flex items-center justify-center shadow-2xl mb-4 md:mb-6 bg-gradient-to-br from-[#FFC72C] to-[#FFD95A] flex-shrink-0">
                <span className="material-symbols-outlined text-white text-3xl md:text-5xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                  {settings.app_icon || 'pets'}
                </span>
              </div>
            )}

            <h1 className="font-display text-xl md:text-4xl font-bold">{settings.site_name}</h1>
            <p className="mt-2 md:mt-3 text-white/80 text-sm md:text-base">Panel Admin</p>

            <div className="hidden md:block mt-8 max-w-sm">
              <p className="text-sm leading-relaxed text-white/75">
                Kelola website dengan mudah melalui dashboard admin.
                Login untuk mengakses semua fitur manajemen konten.
              </p>
            </div>
          </div>

          {/* Sisi Kanan: Form Login */}
          <div className="min-h-0 flex justify-center px-6 md:px-14 pt-10 pb-6 md:py-14 overflow-y-auto scroll-pt-8">
            <div className="w-full max-w-md my-auto">
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">Masuk ke Dashboard</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-8">Silakan login menggunakan akun admin Anda</p>

              {error && (
                <div className="mb-5 p-3.5 rounded-xl text-sm font-medium border flex items-center bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400">
                  <span className="material-symbols-outlined text-base mr-2">warning</span>
                  {error}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-neutral-500 dark:text-neutral-400">Username</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 flex items-center">
                      <span className="material-symbols-outlined text-lg">person</span>
                    </span>
                    <input
                      required autoFocus autoComplete="username" placeholder="admin"
                      value={form.username}
                      onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-neutral-500 dark:text-neutral-400">Password</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 flex items-center">
                      <span className="material-symbols-outlined text-lg">lock</span>
                    </span>
                    <input
                      required type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="••••••••"
                      value={form.password}
                      onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                      className={`${inputCls} pr-11`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
                    >
                      <span className="material-symbols-outlined text-lg">{showPassword ? 'visibility_off' : 'visibility'}</span>
                    </button>
                  </div>
                </div>

                {needsCaptcha && (
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-neutral-500 dark:text-neutral-400">Kode Verifikasi</label>
                    <div className="flex items-center gap-3">
                      <input
                        required placeholder="Masukkan kode"
                        value={form.captcha}
                        onChange={(e) => setForm((f) => ({ ...f, captcha: e.target.value }))}
                        className="flex-1 px-4 py-3 rounded-xl border text-sm outline-none bg-white dark:bg-neutral-800 dark:text-white border-neutral-300 dark:border-neutral-700 focus:outline-none focus:ring-4 focus:ring-forest-500/15 focus:border-forest-500 transition-all"
                      />
                      <img
                        src={`/api/auth/captcha?k=${captchaKey}`}
                        alt="CAPTCHA"
                        title="Klik untuk refresh"
                        onClick={refreshCaptcha}
                        className="h-12 rounded-lg cursor-pointer border border-neutral-200 dark:border-neutral-700"
                      />
                    </div>
                    <p className="text-xs text-neutral-400 mt-1.5">Terlalu banyak percobaan gagal — masukkan kode di gambar untuk melanjutkan.</p>
                  </div>
                )}

                <button
                  disabled={loading} type="submit"
                  className={`w-full py-4 rounded-xl font-bold text-white shadow-md flex items-center justify-center gap-2 disabled:opacity-60 ${MORPH_BTN}`}
                  style={{ background: `linear-gradient(90deg, ${settings.app_color || 'var(--accent)'}, var(--color-forest-600))` }}
                >
                  <span className="material-symbols-outlined text-lg">login</span>
                  {loading ? 'Memproses...' : 'Masuk'}
                </button>
              </form>

              <div className="mt-5 text-center">
                <Link to="/" className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 inline-flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">arrow_back</span> Kembali ke Website
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}